import { Moment } from 'moment-timezone';

import { AcrForm } from 'model/AcrCommon';

export interface InstructionLangauge {
  isoCode: string;
  title: string;
}

export interface ITeacherRequirements {
  skillIds?: string[];
  instructionLanguages: string[];
  departmentIds?: string[];
  instructionLanguage: string | null;
}

export interface IEVCClassEntryRule {
  open: number;
  firstEntry: number;
}

export enum EVCLayoutCode {
  PL = 'adults_pl',
  PLV2 = 'adults_pl_v2',
  GL = 'adults_gl',
  GLV2 = 'adults_gl_v2',
  SPACES = 'adults_spaces',
  PL_SPACES = 'adults_pl_spaces',
  STORE_SPACES = 'store_spaces',
  TECH_CHECK = 'techcheck',
  PLSS = 'adults_pl_ss',
  GLSS = 'adults_gl_ss',
  GL_BREAKOUT_ROOMS = 'gl_breakout_rooms',
  HC_SPACES = 'hyperclass_pl_spaces',
  HC_GL_SPACES = 'hyperclass_gl_spaces',
  HC_GL_LITE = 'hyperclass_gl_lite',
  HC_PL_SS = 'hyperclass_pl_ss',
  HC_GL_SS = 'hyperclass_gl_ss',
}

export enum EVCLocation {
  NV = 'NV',
  SH = 'SH',
  JP = 'JP',
}

export interface IEVCClassroomDef {
  teacher: IEVCClassEntryRule;
  student: IEVCClassEntryRule;
  closeDelay: number;
  location: EVCLocation;
  layout: EVCLayoutCode;
}

export enum ClassroomType {
  NONE = 'none',
  EVC = 'evc',
}

export enum LessonStatusAlgorithm {
  NONE = 'none',
  ASOT_PL20 = 'asot_pl20',
  ASOT_PL40 = 'asot_pl40',
  ASOT_GL45 = 'asot_gl45',
  EFPRO = 'efpro',
  HULT_SL40 = 'hult_sl40',
  HULT_SL80 = 'hult_sl80',
  HULT_SL120 = 'hult_sl120',
  HULT_PL40 = 'hult_pl40',
  HULT_GL45 = 'hult_gl45',
}

export interface IClassroomDef {
  classroomType: ClassroomType;
  evc?: IEVCClassroomDef;
}

export interface IContentDef {
  evc?: IEVCContentDef;
  required?: boolean;
}

export interface IEVCContentDef {
  customContent: boolean;
  lessonType: EVCContentLessonType;
}

export enum EVCContentLessonType {
  Private = 'pl',
  Group = 'gl',
}

export type tLessonType = keyof typeof EVCContentLessonType;

export const EVCContentLessonTypeLessonMapping: Record<EVCContentLessonType, tLessonType> = {
  [EVCContentLessonType.Private]: 'Private',
  [EVCContentLessonType.Group]: 'Group',
};

export interface IAcrConfig {
  acrForm?: AcrForm;
  lateSubmitDelay?: number;
  enableAIContent?: boolean;
}

export interface IOptions {
  name: string;
  value: string;
}

export interface IMeta {
  page: {
    totalPages: number;
    totalItems: number;
  };
}

export interface IResponse<T> {
  data: T[];
  meta: IMeta;
}

export interface IRange {
  start: Moment;
  end: Moment;
}

export interface Department {
  id: string;
  name: string;
  center: string;
}

export interface DepartmentWithSchool extends Department {
  school: School;
}

export interface School {
  id: string;
  name: string;
  chatConfig?: ChatConfig;
  addiChatConfig?: AddiChatConfig;
  helpCenterConfig?: HelpCenterConfig;
  piiConfig?: PiiConfig;
  teacherDashboardConfig?: TeacherDashboardConfig | null;
}

export interface ChatConfig {
  enabled: boolean;
}

export interface AddiChatConfig {
  enabled: boolean;
}

export interface HelpCenterConfig {
  enabled: boolean;
}

/** Per-school switches for withholding student PII from teachers. */
export interface PiiConfig {
  studentEmailHidden: boolean;
}

/** Per-school switches for the teacher dashboard pilot UI. */
export interface TeacherDashboardConfig {
  assignmentsEnabled: boolean | null;
}

export interface AutomationsConfig {
  cancelUnassigned: CancelUnassignedAutomationConfig;
  assignUnassigned: AssignUnassignedAutomationConfig;
}

export interface CancelUnassignedAutomationConfig {
  enabled: boolean;
  bookingStartLtDelay?: number;
}

export interface AssignUnassignedAutomationConfig {
  enabled: boolean;
  bookingStartGteDelay?: number;
}

export const LessonBookingType = {
  Private: 'pl',
  Group: 'gl',
  Scheduled: 'sl',
} as const;

export interface IPrismConfig {
  lessonType?: PrismLessonType;
  courseCode?: PrismCourseCode;
  org?: PrismOrg;
}

export const PrismLessonTypes = [
  'group_lesson',
  'private_lesson',
  'checkpoint',
  'schedule_group_lesson',
  'schedule_private_lesson',
  'face_to_face_group_lessons',
  'face_to_face_private_lessons',
] as const;
export type PrismLessonType = (typeof PrismLessonTypes)[number];

export const PrismLessonTypeLabels: Record<PrismLessonType, string> = {
  group_lesson: 'Group Lesson',
  private_lesson: 'Private Lesson',
  checkpoint: 'Checkpoint',
  schedule_group_lesson: 'Scheduled Group Lesson',
  schedule_private_lesson: 'Scheduled Private Lesson',
  face_to_face_group_lessons: 'Face to Face Group Lesson',
  face_to_face_private_lessons: 'Face to Face Private Lesson',
};

export const PrismCourseCodes = [
  'N/A',
  'GE',
  'GENERAL_GERMAN',
  'GENERAL_FRENCH',
  'GENERAL_SPANISH',
  'GENERAL_SWEDISH',
  'GENERAL_PORTUGUESE',
  'SCHOOL_ENGLISH',
] as const;
export type PrismCourseCode = (typeof PrismCourseCodes)[number];

export const PrismOrgs = ['elive', 'ecc', 'lgp.direct'] as const;
export type PrismOrg = (typeof PrismOrgs)[number];

export type ILessonBookingType = (typeof LessonBookingType)[keyof typeof LessonBookingType];

export interface IGLMatchmakingConfig {
  teacherEntryDelay?: number;
  studentEntryDelay?: number;
  newLessonMatchDelay?: number;
  closeDelay?: number;
  maxStudents?: number;
  holdSeatDuration?: number;
  lobbyFirstEntryDelay?: number;
}

export interface Skill {
  id: string;
  code: string;
  description: string;
  state: string;
}
